import { useState } from "react";
import { X, Minus, Plus, Trash2, Loader2, Gift } from "lucide-react";
import { Sheet, SheetContent } from "./ui/sheet";
import { useCart } from "../context/CartContext";
import api from "../lib/api";
import { inr } from "../lib/format";
import { toast } from "sonner";

export const CartDrawer = () => {
  const { items, open, setOpen, updateQty, removeItem, subtotal } = useCart();
  const [giftWrap, setGiftWrap] = useState(false);
  const [loading, setLoading] = useState(false);
  const [addr, setAddr] = useState({ name: "", phone: "", line1: "", city: "", state: "", pincode: "" });

  const total = subtotal + (giftWrap ? 499 : 0);

  const checkout = async () => {
    if (items.length === 0) return;
    if (!addr.name || !addr.phone || !addr.line1 || !addr.city || !addr.state || !addr.pincode) {
      toast.error("Please fill in your full shipping address");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/payments/checkout", {
        items: items.map((i) => ({ product_id: i.product.id, quantity: i.quantity })),
        origin_url: window.location.origin,
        gift_wrap: giftWrap,
        address: addr,
      });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="bg-obsidian-2 border-gold/20 w-full sm:max-w-md p-0 flex flex-col" data-testid="cart-drawer">
        <div className="flex items-center justify-between px-6 py-5 border-b border-gold/15">
          <h2 className="font-serif text-2xl text-gold-muted">Your Cart</h2>
          <button onClick={() => setOpen(false)} className="text-muted-foreground hover:text-gold" data-testid="cart-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-3 px-6 text-center">
            <p className="font-serif text-xl text-gold-muted">Your cart is empty</p>
            <p className="text-sm text-muted-foreground">Discover handwoven treasures in our collection.</p>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
              {items.map((i) => (
                <div key={i.product.id} className="flex gap-4" data-testid={`cart-item-${i.product.id}`}>
                  <img src={i.product.image} alt="" className="w-20 h-24 object-cover rounded-md gold-border" />
                  <div className="flex-1">
                    <div className="flex justify-between gap-2">
                      <h4 className="font-serif text-base text-gold-muted leading-tight">{i.product.name}</h4>
                      <button onClick={() => removeItem(i.product.id)} data-testid={`cart-remove-${i.product.id}`} className="text-muted-foreground hover:text-destructive">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground">{i.product.color}</p>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-2 border border-gold/25 rounded-md">
                        <button onClick={() => updateQty(i.product.id, i.quantity - 1)} data-testid={`cart-dec-${i.product.id}`} className="p-1.5 text-gold-muted hover:text-gold"><Minus className="w-3.5 h-3.5" /></button>
                        <span className="text-sm w-6 text-center">{i.quantity}</span>
                        <button onClick={() => updateQty(i.product.id, i.quantity + 1)} data-testid={`cart-inc-${i.product.id}`} className="p-1.5 text-gold-muted hover:text-gold"><Plus className="w-3.5 h-3.5" /></button>
                      </div>
                      <span className="font-mono text-gold text-sm">{inr(i.product.price * i.quantity)}</span>
                    </div>
                  </div>
                </div>
              ))}

              <button
                onClick={() => setGiftWrap((g) => !g)}
                data-testid="gift-wrap-toggle"
                className={`w-full flex items-center gap-3 p-3 rounded-md border transition-colors ${giftWrap ? "border-gold bg-gold/10" : "border-gold/20"}`}
              >
                <Gift className={`w-4 h-4 ${giftWrap ? "text-gold" : "text-muted-foreground"}`} />
                <span className="text-sm text-left flex-1">Luxury gift wrapping</span>
                <span className="font-mono text-xs text-gold">+₹499</span>
              </button>
            </div>

            <div className="border-t border-gold/15 px-6 py-5 space-y-4">
              <div data-testid="shipping-address-form">
                <p className="caption mb-2">Shipping address</p>
                <div className="grid grid-cols-2 gap-2">
                  <input value={addr.name} onChange={(e) => setAddr({ ...addr, name: e.target.value })} placeholder="Full name" data-testid="addr-name" className="col-span-2 bg-obsidian-2 border border-gold/25 rounded px-2 py-1.5 text-sm text-gold-muted focus:outline-none focus:border-gold" />
                  <input value={addr.phone} onChange={(e) => setAddr({ ...addr, phone: e.target.value })} placeholder="Phone" data-testid="addr-phone" className="bg-obsidian-2 border border-gold/25 rounded px-2 py-1.5 text-sm text-gold-muted focus:outline-none focus:border-gold" />
                  <input value={addr.pincode} onChange={(e) => setAddr({ ...addr, pincode: e.target.value })} placeholder="Pincode" data-testid="addr-pincode" className="bg-obsidian-2 border border-gold/25 rounded px-2 py-1.5 text-sm text-gold-muted focus:outline-none focus:border-gold" />
                  <input value={addr.line1} onChange={(e) => setAddr({ ...addr, line1: e.target.value })} placeholder="Address" data-testid="addr-line1" className="col-span-2 bg-obsidian-2 border border-gold/25 rounded px-2 py-1.5 text-sm text-gold-muted focus:outline-none focus:border-gold" />
                  <input value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} placeholder="City" data-testid="addr-city" className="bg-obsidian-2 border border-gold/25 rounded px-2 py-1.5 text-sm text-gold-muted focus:outline-none focus:border-gold" />
                  <input value={addr.state} onChange={(e) => setAddr({ ...addr, state: e.target.value })} placeholder="State" data-testid="addr-state" className="bg-obsidian-2 border border-gold/25 rounded px-2 py-1.5 text-sm text-gold-muted focus:outline-none focus:border-gold" />
                </div>
              </div>
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>Shipping</span><span className="text-gold">Insured · at cost</span>
              </div>
              <div className="flex justify-between items-baseline">
                <span className="text-gold-muted">Total</span>
                <span className="font-mono text-2xl text-gold" data-testid="cart-total">{inr(total)}</span>
              </div>
              <button
                onClick={checkout}
                disabled={loading}
                data-testid="checkout-button"
                className="w-full bg-gold text-obsidian font-semibold py-3 rounded-full hover:bg-gold-light transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {loading ? "Redirecting…" : "Checkout securely"}
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
